import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { TextField, CircularProgress, Alert, InputAdornment, IconButton } from '@mui/material';
import { Eye, EyeOff, Mail, Lock, ArrowRight, Building2, MapPin } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const inputSx = {
  '& .MuiOutlinedInput-root': {
    background:'#faf8f5', borderRadius:'12px !important', fontSize:14, fontFamily:'DM Sans,sans-serif',
    '& fieldset':{ borderColor:'var(--border)' },
    '&:hover fieldset':{ borderColor:'#d4c4b0' },
    '&.Mui-focused fieldset':{ borderColor:'var(--brand)' },
  },
  '& .MuiInputLabel-root':{ fontSize:14, fontFamily:'DM Sans,sans-serif', color:'var(--text-muted)' },
  '& .MuiInputLabel-root.Mui-focused':{ color:'var(--brand)' },
};

export const LoginPage: React.FC = () => {
  const logoPath = "/adrojlogo.png";
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); setError(''); setLoading(true);
    try {
      await login(email, password);
      navigate(email === 'admin@adroj.in' ? '/admin/dashboard' : '/brand/dashboard');
    } catch { setError('Invalid credentials. Please try again.'); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight:'100vh', background:'var(--bg-base)', display:'flex' }}>
      {/* Left panel */}
      <div style={{ flex:'0 0 45%', background:'linear-gradient(145deg, #1a0f07 0%, #3d1e0a 50%, #1a0f07 100%)', display:'flex', flexDirection:'column', justifyContent:'space-between', padding:'48px', position:'relative', overflow:'hidden' }} className="hidden md:flex" >
        {/* Decorative circles */}
        <div style={{ position:'absolute', top:-80, right:-80, width:300, height:300, borderRadius:'50%', border:'1px solid rgba(232,98,26,0.15)' }} />
        <div style={{ position:'absolute', top:20, right:20, width:200, height:200, borderRadius:'50%', border:'1px solid rgba(232,98,26,0.08)' }} />
        <div style={{ position:'absolute', bottom:-100, left:-60, width:350, height:350, borderRadius:'50%', border:'1px solid rgba(232,98,26,0.1)' }} />
        <div style={{ position:'absolute', bottom:100, left:40, width:6, height:6, borderRadius:'50%', background:'rgba(232,98,26,0.6)' }} />
        <div style={{ position:'absolute', top:160, right:120, width:4, height:4, borderRadius:'50%', background:'rgba(232,98,26,0.5)' }} />

        <div style={{ position:'relative', zIndex:1 }}>
          <div style={{ display:'flex', alignItems:'center' }}>
            <img
              src={logoPath}
              alt="Adroj Logo"
              style={{
                height: 90,
                width: 'auto',
                objectFit: 'contain'
              }}
            />
          </div>
          {/* <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <div style={{ width:38, height:38, borderRadius:11, background:'linear-gradient(135deg, #e8621a, #c2490e)', display:'flex', alignItems:'center', justifyContent:'center', boxShadow:'0 6px 20px rgba(232,98,26,0.4)' }}>
              <span style={{ color:'#fff', fontFamily:'Playfair Display,serif', fontWeight:700, fontSize:20 }}>A</span>
            </div>
            <span style={{ color:'#fff', fontFamily:'Playfair Display,serif', fontWeight:700, fontSize:22 }}>adroj<span style={{ color:'#e8621a' }}>.</span></span>
          </div> */}
        </div>

        <div style={{ position:'relative', zIndex:1 }}>
          <h2 style={{ color:'#fff', fontFamily:'Playfair Display,serif', fontWeight:700, fontSize:36, lineHeight:1.2, margin:'0 0 20px' }}>India's Premier Advertising Space Marketplace</h2>
          <p style={{ color:'rgba(255,255,255,0.5)', fontFamily:'DM Sans,sans-serif', fontSize:15, lineHeight:1.7, margin:0 }}>Connect brands with the best outdoor advertising spaces across 200+ cities. From billboards to digital screens — all in one platform.</p>
          <div style={{ display:'flex', gap:24, marginTop:36 }}>
            {[{ val:'500+', label:'Ad Spaces' }, { val:'50+', label:'Cities' }, { val:'200+', label:'Brands' }].map(s => (
              <div key={s.label}>
                <div style={{ color:'#e8621a', fontFamily:'Playfair Display,serif', fontWeight:700, fontSize:26 }}>{s.val}</div>
                <div style={{ color:'rgba(255,255,255,0.4)', fontFamily:'DM Sans,sans-serif', fontSize:12 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ position:'relative', zIndex:1, display:'flex', gap:8, alignItems:'center' }}>
          <MapPin size={14} color="rgba(255,255,255,0.3)"/>
          <span style={{ color:'rgba(255,255,255,0.3)', fontFamily:'DM Sans,sans-serif', fontSize:12 }}>Mumbai · Delhi · Bangalore · Hyderabad · Pune · Chennai</span>
        </div>
      </div>

      {/* Right: Form */}
      <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', padding:'40px 24px' }}>
        <div style={{ width:'100%', maxWidth:420 }}>
          {/* Mobile logo */}
          <div style={{ textAlign:'center', marginBottom:32 }} className="block md:hidden">
            <div style={{ width:48, height:48, borderRadius:14, background:'linear-gradient(135deg,#e8621a,#c2490e)', display:'inline-flex', alignItems:'center', justifyContent:'center', marginBottom:12 }}>
              <span style={{ color:'#fff', fontFamily:'Playfair Display,serif', fontWeight:700, fontSize:24 }}>A</span>
            </div>
            <h1 style={{ color:'var(--text-primary)', fontFamily:'Playfair Display,serif', fontWeight:700, fontSize:24, margin:0 }}>adroj<span style={{ color:'var(--brand)' }}>.</span></h1>
          </div>

          <h2 style={{ color:'var(--text-primary)', fontWeight:700, fontSize:26, margin:'0 0 6px', letterSpacing:'-0.02em' }}>Welcome back</h2>
          <p style={{ color:'var(--text-muted)', fontSize:14, margin:'0 0 32px' }}>Sign in to your account to continue</p>

          {error && <Alert severity="error" sx={{ mb:2.5, borderRadius:'12px !important' }}>{error}</Alert>}

          <form onSubmit={handleSubmit} style={{ display:'flex', flexDirection:'column', gap:16 }}>
            <TextField label="Email Address" type="email" value={email} onChange={e=>setEmail(e.target.value)} required fullWidth sx={inputSx}
              InputProps={{ startAdornment:<InputAdornment position="start"><Mail size={15} color="var(--text-muted)"/></InputAdornment> }}/>
            <TextField label="Password" type={showPw?'text':'password'} value={password} onChange={e=>setPassword(e.target.value)} required fullWidth sx={inputSx}
              InputProps={{ startAdornment:<InputAdornment position="start"><Lock size={15} color="var(--text-muted)"/></InputAdornment>, endAdornment:<InputAdornment position="end"><IconButton onClick={()=>setShowPw(!showPw)} size="small">{showPw?<EyeOff size={15}/>:<Eye size={15}/>}</IconButton></InputAdornment> }}/>

            <button type="submit" disabled={loading} className="btn-brand" style={{ padding:'13px 24px', fontSize:15, display:'flex', alignItems:'center', justifyContent:'center', gap:8, marginTop:4 }}>
              {loading?<CircularProgress size={18} color="inherit"/>:<>Sign In <ArrowRight size={16}/></>}
            </button>
          </form>

          <p style={{ textAlign:'center', color:'var(--text-muted)', fontSize:13, marginTop:24 }}>
            Don't have an account?{' '}
            <Link to="/register" style={{ color:'var(--brand)', fontWeight:600, textDecoration:'none' }}>Register here</Link>
          </p>

          {/* Demo creds */}
          <div style={{ marginTop:28, background:'var(--bg-muted)', borderRadius:14, padding:'14px 16px', border:'1px solid var(--border)' }}>
            <div style={{ color:'var(--text-muted)', fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:10 }}>Demo Credentials</div>
            <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
              {[{ role:'Brand Company', email:'brand@company.com', icon:'🏢' }, { role:'Admin', email:'admin@adroj.in', icon:'⚙️' }].map(c => (
                <div key={c.role} onClick={()=>setEmail(c.email)} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', background:'#fff', borderRadius:10, padding:'9px 12px', cursor:'pointer', border:'1px solid var(--border)', transition:'all 0.18s' }}
                  onMouseEnter={e=>(e.currentTarget).style.borderColor='var(--brand)'}
                  onMouseLeave={e=>(e.currentTarget).style.borderColor='var(--border)'}>
                  <span style={{ fontSize:13, color:'var(--text-primary)', fontWeight:500 }}>{c.icon} {c.role}</span>
                  <span style={{ fontSize:11, color:'var(--text-muted)', fontFamily:'monospace' }}>{c.email}</span>
                </div>
              ))}
            </div>
            <p style={{ color:'var(--text-muted)', fontSize:11, margin:'8px 0 0' }}>Click a role above to auto-fill. Any password works.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export const RegisterPage: React.FC = () => {
  const logoPath = "/adrojlogo.png";
  const [form, setForm] = useState({ companyName:'', contactPerson:'', email:'', mobile:'', gst:'', password:'', billingAddress:'' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => setForm(f=>({ ...f, [e.target.name]:e.target.value }));
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); setLoading(true);
    await new Promise(r=>setTimeout(r,1000));
    setLoading(false); setSuccess(true);
    setTimeout(()=>navigate('/login'),2200);
  };

  return (
    <div style={{ minHeight:'100vh', background:'var(--bg-base)', display:'flex', alignItems:'center', justifyContent:'center', padding:24 }}>
      <div style={{ width:'100%', maxWidth:520 }}>
        {/* <div style={{ textAlign:'center', marginBottom:28 }}>
          <Link to="/login" style={{ display:'inline-flex', alignItems:'center', gap:8, textDecoration:'none', marginBottom:24 }}>
            <div style={{ width:40, height:40, borderRadius:12, background:'linear-gradient(135deg,#e8621a,#c2490e)', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <span style={{ color:'#fff', fontFamily:'Playfair Display,serif', fontWeight:700, fontSize:20 }}>A</span>
            </div>
            <span style={{ color:'var(--text-primary)', fontFamily:'Playfair Display,serif', fontWeight:700, fontSize:20 }}>adroj<span style={{ color:'var(--brand)' }}>.</span></span>
          </Link>
        </div> */}
        <div style={{ textAlign:'center', marginBottom:28 }}>
          <Link
            to="/login"
            style={{
              display:'inline-flex',
              alignItems:'center',
              justifyContent:'center',
              textDecoration:'none',
              marginBottom:24
            }}
          >
            <img
              src="/adrojlogo.png"
              alt="Adroj Logo"
              style={{
                height: 85,
                width: 'auto',
                objectFit: 'contain'
              }}
            />
          </Link>
        </div>

        <div style={{ background:'#fff', border:'1px solid var(--border)', borderRadius:20, padding:36, boxShadow:'var(--shadow-md)' }}>
          {success ? (
            <div style={{ textAlign:'center', padding:'20px 0' }}>
              <div style={{ width:60, height:60, borderRadius:'50%', background:'var(--success-bg)', display:'inline-flex', alignItems:'center', justifyContent:'center', marginBottom:16, fontSize:28 }}>✅</div>
              <h3 style={{ color:'var(--success)', fontWeight:700, fontSize:20, margin:'0 0 8px' }}>Registration Successful!</h3>
              <p style={{ color:'var(--text-muted)', fontSize:14 }}>Please verify your email. Redirecting to login...</p>
            </div>
          ) : (
            <>
              <h2 style={{ color:'var(--text-primary)', fontWeight:700, fontSize:22, margin:'0 0 4px', letterSpacing:'-0.02em' }}>Create Account</h2>
              <p style={{ color:'var(--text-muted)', fontSize:13, margin:'0 0 24px' }}>Register your company to start booking ad spaces</p>
              <form onSubmit={handleSubmit} style={{ display:'flex', flexDirection:'column', gap:14 }}>
                {[
                  { name:'companyName', label:'Company Name' },
                  { name:'contactPerson', label:'Contact Person' },
                  { name:'email', label:'Email Address', type:'email' },
                  { name:'mobile', label:'Mobile Number', type:'tel' },
                  { name:'gst', label:'GST Number' },
                  { name:'billingAddress', label:'Billing Address' },
                  { name:'password', label:'Password', type:'password' },
                ].map(f => (
                  <TextField key={f.name} name={f.name} label={f.label} type={f.type||'text'} value={(form as any)[f.name]} onChange={handleChange} required fullWidth sx={inputSx} size="small"/>
                ))}
                <button type="submit" disabled={loading} className="btn-brand" style={{ padding:'13px', fontSize:14, marginTop:4, display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
                  {loading?<CircularProgress size={18} color="inherit"/>:'Create Account'}
                </button>
                <p style={{ textAlign:'center', color:'var(--text-muted)', fontSize:13, margin:0 }}>
                  Already have an account? <Link to="/login" style={{ color:'var(--brand)', fontWeight:600, textDecoration:'none' }}>Sign in</Link>
                </p>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
